import React, { useEffect, memo, useCallback } from "react";
import { createPortal } from "react-dom";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { FUTURES } from "@constants";
import "./index.sass";
import { NOTIFICATION_TYPE, notificationEvent } from "@actions/futures";
import { useTranslation } from "@helpers/translate";
import { normalizeOrderSide } from "@helpers/formatOrders";
import CrossIcon from "@assets/icons/CrossIcon";
import TradeArrowUpIcon from "@assets/icons/TradeArrowUpIcon";
import TradeArrowDownIcon from "@assets/icons/TradeArrowDownIcon";
import { ReactComponent as SuccessIcon } from "@assets/images/icons/toast-success.svg";
import RiskErrorIcon from "@assets/icons/RiskErrorIcon";
import {
  LONG_COLOR,
  SHORT_COLOR,
} from "../Controls/TradeForm/constants";
import {
  formatSignedPnl,
  getTone,
  stripQuoteAsset,
} from "../Orders/positionFormat";
import { useSwipeDismiss } from "./useSwipeDismiss";

// docs/trade-logic.md 3: toasts stay 5 s, the timer pauses on hover
const TOAST_DURATION = 5000;
// Older toasts peek out from under the newest one, as in the design
const MAX_VISIBLE = 3;
const PARTIAL_PERCENT_DECIMALS = 2;

const toNumber = (value) => {
  const numeric = Number(String(value ?? "").replace(/,/g, ""));

  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * "Your ↑BTC trade …" with the direction arrow and the ticker in bold.
 * prefix replaces "Your", e.g. "You successfully cashed out 50.00% of your".
 */
const TradeText = ({ notification, prefix, ending, children }) => {
  const { __ } = useTranslation();
  const { isSellSide } = normalizeOrderSide(notification.side);
  const ticker = stripQuoteAsset(
    String(notification.symbol ?? "").toUpperCase(),
  );

  return (
    <p className="futures-toast__text">
      {prefix ?? __("futures.toast.your")}
      <span className="futures-toast__ticker">
        {isSellSide ? (
          <TradeArrowDownIcon size={16} color={SHORT_COLOR} />
        ) : (
          <TradeArrowUpIcon size={16} color={LONG_COLOR} />
        )}
        {ticker}
      </span>
      {ending}
      {children}
    </p>
  );
};

/**
 * "… was closed for +$4.20", the PnL colored by its sign.
 */
const ClosedTradeText = ({ notification, prefix, ending }) => {
  const pnl = toNumber(notification.pnl);
  const tone = getTone(pnl);

  return (
    <TradeText notification={notification} prefix={prefix} ending={ending}>
      {pnl != null && (
        <span
          className={classnames("futures-toast__pnl", {
            [`futures-toast__pnl--${tone}`]: tone,
          })}
        >
          {formatSignedPnl(pnl)}
        </span>
      )}
    </TradeText>
  );
};

// Title of the toasts of a position closed in full
const CLOSED_TITLE = {
  [NOTIFICATION_TYPE.POSITION_CLOSED]: "futures.toast.trade_closed",
  [NOTIFICATION_TYPE.TAKE_PROFIT_HIT]: "futures.toast.take_profit_hit",
  [NOTIFICATION_TYPE.STOP_LOSS_HIT]: "futures.toast.stop_loss_hit",
  [NOTIFICATION_TYPE.POSITION_LIQUIDATED]: "futures.toast.bet_liquidated",
};

const ToastContent = ({ notification }) => {
  const { __ } = useTranslation();

  switch (notification.notificationType) {
  case NOTIFICATION_TYPE.POSITION_OPENED:
    return (
      <>
        <h3 className="futures-toast__title">
          {__("futures.toast.trade_opened")}
        </h3>
        <TradeText
          notification={notification}
          ending={__("futures.toast.is_live")}
        />
      </>
    );

  case NOTIFICATION_TYPE.POSITION_CLOSED:
  case NOTIFICATION_TYPE.TAKE_PROFIT_HIT:
  case NOTIFICATION_TYPE.STOP_LOSS_HIT:
  case NOTIFICATION_TYPE.POSITION_LIQUIDATED:
    return (
      <>
        <h3 className="futures-toast__title">
          {__(CLOSED_TITLE[notification.notificationType])}
        </h3>
        <ClosedTradeText
          notification={notification}
          ending={__("futures.toast.closed_for")}
        />
      </>
    );

  case NOTIFICATION_TYPE.POSITION_PARTIALLY_CLOSED:
    return (
      <>
        <h3 className="futures-toast__title">
          {__("futures.toast.trade_partially_closed")}
        </h3>
        <TradeText
          notification={notification}
          prefix={`${__("futures.toast.cashed_out")} ${Number(notification.percent).toFixed(PARTIAL_PERCENT_DECIMALS)}% ${__("futures.toast.of_your")}`}
          ending={__("futures.toast.partial_ending")}
        />
      </>
    );

  case "orderOpened":
    return (
      <>
        <h3 className="futures-toast__title">{__("futures.order_opened")}</h3>
        <p className="futures-toast__text">{__("futures.order_success")}</p>
      </>
    );

  case "orderClosed":
    return (
      <>
        <h3 className="futures-toast__title">{__("futures.order_closed")}</h3>
        <p className="futures-toast__text">
          {__("futures.order_close_success")}
        </p>
      </>
    );

  case NOTIFICATION_TYPE.RISK_VALIDATION_ERROR:
    return (
      <>
        <h3 className="futures-toast__title">{notification.title}</h3>
        <p className="futures-toast__text">{notification.text}</p>
      </>
    );

  default:
    return <h3 className="futures-toast__title">{notification.text}</h3>;
  }
};

const SwipeableToast = ({ notification, depth, onClose, closeLabel }) => {
  const handleClose = useCallback(() => onClose(notification.id), [notification.id, onClose]);
  const { style, handlers } = useSwipeDismiss(handleClose);

  return (
    <div
      className="futures-toast"
      style={{ "--futures-toast-depth": depth, ...style }}
      role="status"
      aria-hidden={depth > 0}
      {...handlers}
    >
      {notification.notificationType === NOTIFICATION_TYPE.RISK_VALIDATION_ERROR ? (
        <RiskErrorIcon />
      ) : (
        <SuccessIcon className="futures-toast__icon" />
      )}

      <div className="futures-toast__body">
        <ToastContent notification={notification} />
      </div>

      <button
        type="button"
        className="futures-toast__close"
        onClick={handleClose}
        aria-label={closeLabel}
      >
        <CrossIcon size={16} />
      </button>

      <span
        className="futures-toast__progress"
        style={{ animationDuration: `${TOAST_DURATION}ms` }}
        onAnimationEnd={handleClose}
      />
    </div>
  );
};

// Success toasts only, errors are shown by the global alert
export const FuturesNotification = memo(() => {
  const { notifications } = useSelector((state) => state.futures);
  const dispatch = useDispatch();
  const { __ } = useTranslation();
  const visible = (notifications ?? []).slice(-MAX_VISIBLE).reverse();

  const handleClose = useCallback((id) => {
    dispatch({ type: FUTURES.HIDE_NOTIFICATION, payload: id });
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = dispatch(notificationEvent());
    return () => {
      unsubscribe();
    };
  }, []);

  return createPortal(
    <div
      className={`futures-toasts ${visible.length ? "is-active" : "is-empty"}`}
      style={{ "--futures-toasts-count": visible.length }}
    >
      {visible.map((notification, depth) => (
        <SwipeableToast
          key={notification.id ?? depth}
          notification={notification}
          depth={depth}
          onClose={handleClose}
          closeLabel={__("futures.trade_modals.close")}
        />
      ))}
    </div>,
    document.body,
  );
});
